import { useState, useCallback } from "react";
import type { GoogleDriveFile } from "@/lib/hooks/google-drive/use-google-drive-browser";
import type { FileAction } from "../types";
import { getDefaultExportFormat } from "../utils/export-formats";
import { downloadDriveFile } from "../utils/download-utils";
import { toast } from "sonner";

interface UseGoogleDriveFileActionsProps {
  isSecondAccount: boolean;
  accessToken: string | undefined;
  onFileSelect?: (file: GoogleDriveFile) => void;
  onPreviewOpen?: (file: GoogleDriveFile) => void;
}

export function useGoogleDriveFileActions({
  isSecondAccount,
  accessToken,
  onFileSelect,
  onPreviewOpen,
}: UseGoogleDriveFileActionsProps) {
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<GoogleDriveFile | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileAction = useCallback(
    async (
      file: GoogleDriveFile,
      action: FileAction,
      exportFormat?: string
    ): Promise<void> => {
      switch (action) {
        case "preview":
          setPreviewFile(file);
          setShowPreview(true);
          onPreviewOpen?.(file);
          break;
        case "view":
          if (file.webViewLink) {
            window.open(file.webViewLink, "_blank");
          } else {
            toast.error("No view link available for this file");
          }
          break;
        case "download": {
          if (!file.canDownload && !file.isGoogleDoc) {
            toast.error("File cannot be downloaded due to permissions");
            return;
          }

          setIsDownloading(file.id);
          try {
            const resolvedFormat = file.isGoogleDoc
              ? (exportFormat ?? getDefaultExportFormat(file.mimeType))
              : undefined;

            const ext = resolvedFormat ? `.${resolvedFormat}` : null;
            const fileName =
              file.isGoogleDoc && ext
                ? file.name.replace(/\.[^/.]+$/, "") + ext
                : file.name;

            await downloadDriveFile(
              file.id,
              fileName,
              accessToken || "",
              isSecondAccount,
              resolvedFormat
            );

            toast.success(`${file.isGoogleDoc ? "Exported" : "Downloaded"}: ${fileName}`);
          } catch (error) {
            const message = error instanceof Error ? error.message : "Unknown error";
            toast.error(`${file.isGoogleDoc ? "Export" : "Download"} failed: ${message}`);
          } finally {
            setIsDownloading(null);
          }
          break;
        }
        case "select":
          onFileSelect?.(file);
          break;
      }
    },
    [isSecondAccount, accessToken, onFileSelect, onPreviewOpen]
  );

  return {
    handleFileAction,
    isDownloading,
    previewFile,
    setPreviewFile,
    showPreview,
    setShowPreview,
  };
}
